// stores/cartStore.ts
import type { IFormattedOrder, OrderResponse } from '@a24/av-sdk/order-sdk'
import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'

export type CartStoreState = {
  order?: OrderResponse['data']
  orderFormatted?: IFormattedOrder
  orderItemCount: number
  cartTimeout?: Date
  customerId?: string
  // Hydration tracking for SSR
  _hasHydrated: boolean
}

export type CartStoreActions = {
  // Hydration
  setHasHydrated: (state: boolean) => void

  // Actions
  reset: () => void
  setOrder: (order: OrderResponse['data']) => void
  setOrderFormatted: (orderFormatted: IFormattedOrder) => void
  setCartTimeout: (timeout: Date) => void
  setCustomerId: (customerId: string) => void
  incrementOrderItemCount: () => void
  decrementOrderItemCount: () => void
  resetOrderCount: () => void
}

export type CartStore = CartStoreState & CartStoreActions

const useCartStore = create<CartStore>()(
  persist(
    (set, get) => ({
      // Initial state
      order: undefined,
      orderFormatted: undefined,
      orderItemCount: 0,
      cartTimeout: undefined,
      customerId: undefined,
      _hasHydrated: false,

      // Hydration
      setHasHydrated: (state) => {
        set({ _hasHydrated: state })
      },

      // Actions
      reset: () => {
        set({
          order: undefined,
          orderFormatted: undefined,
          cartTimeout: undefined,
          customerId: undefined
        })
      },

      setOrder: (order) => {
        set({ order })
      },

      setOrderFormatted: (orderFormatted) => {
        set({ orderFormatted })
      },

      setCartTimeout: (timeout) => {
        set({ cartTimeout: timeout })
      },

      setCustomerId: (customerId) => {
        set({ customerId })
      },

      incrementOrderItemCount: () => {
        const currentCount = get().orderItemCount
        set({ orderItemCount: currentCount + 1 })
      },

      decrementOrderItemCount: () => {
        const currentCount = get().orderItemCount
        if (currentCount > 0) {
          set({ orderItemCount: currentCount - 1 })
        }
      },

      resetOrderCount: () => {
        set({ orderItemCount: 0 })
      }
    }),
    {
      name: 'cart-storage',
      storage: createJSONStorage(() => {
        // SSR-safe storage
        if (typeof window === 'undefined') {
          return {
            getItem: () => null,
            setItem: () => {},
            removeItem: () => {}
          }
        }
        return localStorage
      }),
      // Only persist the cart data, not hydration state
      partialize: (state) => ({
        order: state.order,
        orderItemCount: state.orderItemCount,
        orderFormatted: state.orderFormatted,
        cartTimeout: state.cartTimeout,
        customerId: state.customerId
      }),
      onRehydrateStorage: () => (state) => {
        // Mark as hydrated after rehydration
        state?.setHasHydrated(true)
      }
    }
  )
)

export default useCartStore
